/**
 * Hydration State Manager
 * Manages the transition from SSR to client-side rendering
 * Ensures proper meta tag updates during navigation
 */
import { debugLog } from '@/utils/debugLogger';

export class HydrationManager {
  private static instance: HydrationManager;
  private hydrationComplete = false;
  private routeChangeListeners: Set<() => void> = new Set();
  private hydrationListeners: Set<() => void> = new Set();

  private constructor() {
    this.initialize();
  }

  static getInstance(): HydrationManager {
    if (!HydrationManager.instance) {
      HydrationManager.instance = new HydrationManager();
    }
    return HydrationManager.instance;
  }

  private initialize() {
    if (typeof window === 'undefined') return;

    // Set initial hydration flag
    (window as any).__INITIAL_SSR_HYDRATION__ = true;

    // 🛡️ FIX: REMOVED auto-complete timeout
    // completeHydration() is now called ONLY by entry-client.tsx after hydrateRoot() + double rAF
    // This ensures the signal is tied to actual React hydration, not an arbitrary timer

    // Listen for route changes (React Router navigation)
    this.setupRouteChangeListener();
  }

  private setupRouteChangeListener() {
    if (typeof window === 'undefined') return;

    // Listen for React Router navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      originalPushState.apply(window.history, args);
      this.onRouteChange();
    };

    window.history.replaceState = (...args) => {
      originalReplaceState.apply(window.history, args);
      this.onRouteChange();
    };

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', () => {
      this.onRouteChange();
    });
  }

  private onRouteChange() {
    // Clear SSR markers on route change to allow client-side updates
    this.clearSSRMarkers();
    
    // Notify listeners
    this.routeChangeListeners.forEach(listener => listener());
  }

  private clearSSRMarkers() {
    if (typeof window === 'undefined') return;

    // Clear route-specific SSR markers
    const windowKeysToClear = [
      '__COMPRO_ORO_CITY_PRE_TITLE__',
      '__COMPRO_ORO_CITY_PRE_DESCRIPTION__',
      '__COMPRO_ORO_CITY_PRE_CANONICAL__',
      '__BLOG_POST__',
      '__BLOG_DATA__',
      '__BLOG_METADATA__',
      '__BLOG_CATEGORY_METADATA__',
      '__PREDICTIONS_DATA__',
      '__SERVER_DEALERS__',
      '__SERVER_CITIES__',
      '__SERVER_FORMATTED_CITY__',
      '__DEALER_STATS',
      '__METAL_PRICES__',
      '__COMPRO_ORO_CITTA_DATA__',
      '__CITIES_STATS__',
      '__AGGREGATED_STATS__',
      '__FAQ_DATA__',
      '__SSR_CONTEXT__',
      '__INITIAL_DEALER_DATA__',
      '__DEALER_DETAIL_DATA__',
      '__COMPRO_ORO_DATA__',
      '__COMPRO_ORO_DATA_DATA__'
    ];

    windowKeysToClear.forEach((key) => {
      if ((window as any)[key] !== undefined) {
        delete (window as any)[key];
      }
    });

    // Remove SSR data scripts from DOM to avoid stale hydration data
    if (typeof document !== 'undefined') {
      const scriptIds = [
        '__BLOG_DATA__',
        '__COMPRO_ORO_CITTA_DATA__',
        '__FAQ_DATA__',
        '__PREDICTIONS_DATA__',
        '__SSR_CONTEXT__',
        '__COMPRO_ORO_DATA__',
        '__DEALER_DATA__',
        '__DEALER_DETAIL_DATA__'
      ];
      scriptIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    }
    
    // Keep global markers but mark as navigation
    (window as any).__IS_NAVIGATION__ = true;
  }

  completeHydration() {
    if (typeof window === 'undefined') return;
    if (this.hydrationComplete) return; // Prevent multiple completions

    this.hydrationComplete = true;
    (window as any).__HYDRATION_COMPLETE__ = true;
    delete (window as any).__INITIAL_SSR_HYDRATION__;

    debugLog('ssr', 'Hydration complete - client-side SEO updates enabled', undefined, 'HydrationManager');
    
    // 🛡️ Notify all listeners that hydration is complete
    this.hydrationListeners.forEach(listener => {
      try {
        listener();
      } catch (e) {
        // Ignore listener errors
      }
    });
    this.hydrationListeners.clear();
  }

  isHydrationComplete(): boolean {
    return this.hydrationComplete;
  }

  /**
   * 🛡️ Subscribe to hydration complete event
   * Callback is called immediately if hydration is already complete,
   * otherwise it's queued and called when completeHydration() fires.
   */
  onHydrationComplete(callback: () => void): void {
    if (this.hydrationComplete) {
      // Already complete, call immediately
      try {
        callback();
      } catch (e) {
        // Ignore
      }
    } else {
      this.hydrationListeners.add(callback);
    }
  }

  onRouteChangeEvent(callback: () => void): () => void {
    this.routeChangeListeners.add(callback);
    
    // Return cleanup function
    return () => {
      this.routeChangeListeners.delete(callback);
    };
  }

  /**
   * Check if we need to cleanup duplicates from initial SSR
   */
  shouldCleanupDuplicates(): boolean {
    if (typeof window === 'undefined') return false;

    // Only cleanup during initial hydration, not during navigation
    return !!(window as any).__INITIAL_SSR_HYDRATION__ && !this.hydrationComplete;
  }
}

// Initialize the manager
if (typeof window !== 'undefined') {
  HydrationManager.getInstance();
}

// Export convenience functions
export const getHydrationManager = () => HydrationManager.getInstance();
export const isHydrationComplete = () => HydrationManager.getInstance().isHydrationComplete();

/**
 * 🛡️ Wait for hydration to complete (returns a Promise)
 * Resolves immediately if hydration is already complete.
 * Has a built-in timeout (default 2000ms) to prevent infinite waiting.
 */
export function waitForHydrationComplete(timeoutMs = 2000): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      // SSR: resolve immediately (no hydration concept)
      resolve();
      return;
    }
    
    const manager = HydrationManager.getInstance();
    
    if (manager.isHydrationComplete()) {
      resolve();
      return;
    }
    
    // Set up timeout as safety net
    const timeoutId = setTimeout(() => {
      debugLog('ssr', 'waitForHydrationComplete timed out, resolving anyway', { timeoutMs }, 'HydrationManager');
      resolve();
    }, timeoutMs);
    
    // Subscribe to hydration complete
    manager.onHydrationComplete(() => {
      clearTimeout(timeoutId);
      resolve();
    });
  });
}
